import {
  Box,
  Button,
  Modal,
  ModalProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import React, { useCallback, useRef, useState } from "react";

export interface UseModalProps {
  title: string | React.ReactNode;
  onConfirm?: () => void;
  loading?: boolean;
  disabled?: boolean;
  size?: ModalProps.Size;
  content: React.ReactNode;
  additionalActions?: React.ReactNode | React.ReactNode[];
  /** Label for the primary confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
}

const useModal = (props: UseModalProps) => {
  const [visible, setVisible] = useState(false);

  // Keep the latest props in a ref so ``ModalComponent`` can have a STABLE
  // identity across renders. If the component identity changed every render
  // (the naive ``const ModalComponent = () => ...`` form), React would
  // unmount and remount the entire modal subtree on each parent state
  // change — resetting transient child state such as an open Select
  // dropdown and causing visible flicker. Reading from a ref lets the
  // content stay current while the component type stays stable.
  const propsRef = useRef(props);
  propsRef.current = props;

  const toggle = useCallback(() => {
    setVisible((prev) => !prev);
  }, []);

  const handleConfirm = useCallback(async () => {
    const { onConfirm } = propsRef.current;
    if (!onConfirm) return;
    try {
      await onConfirm();
    } catch {
      // Keep the modal open so the user can retry. onConfirm owns surfacing
      // the failure (e.g. an error notification); swallowing here also avoids
      // an unhandled rejection from the button's onClick handler.
      return;
    }
    toggle();
  }, [toggle]);

  const ModalComponent = useCallback(() => {
    const {
      title,
      loading,
      disabled,
      content,
      additionalActions,
      size = "medium",
      onConfirm,
      confirmLabel = "Confirm",
    } = propsRef.current;

    return (
      <Modal
        size={size}
        visible={visible}
        onDismiss={toggle}
        header={title}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggle} disabled={loading}>
                {onConfirm ? "Cancel" : "Close"}
              </Button>
              {additionalActions}
              {onConfirm && (
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={disabled || loading}
                  loading={loading}
                >
                  {confirmLabel}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
      >
        {content}
      </Modal>
    );
  }, [visible, toggle, handleConfirm]);

  return {
    toggle,
    ModalComponent,
    visible,
  };
};

export default useModal;
